import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useTheme } from '../../features/theme/context/ThemeContext';
import './VerificationModals.css';

function PhoneVerificationModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  phoneNumber,
  title = "Verify Phone Number Change"
}) {
  const { darkMode } = useTheme();
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [canResend, setCanResend] = useState(true);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setVerificationCode(['', '', '', '', '', '']);
      setError('');
      setSuccessMessage('');
      setIsLoading(false);
      setTimeLeft(0);
      setCanResend(true);
      // Focus first input after a short delay to ensure modal is rendered
      setTimeout(() => {
        document.getElementById('modal-code-0')?.focus();
      }, 100);
    }
  }, [isOpen]);

  useEffect(() => {
    let interval;
    if (timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prevTimeLeft) => prevTimeLeft - 1);
      }, 1000);
    } else {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [timeLeft]);

  useEffect(() => {
    let timeout;
    if (error || successMessage) {
      timeout = setTimeout(() => {
        setError('');
        setSuccessMessage('');
      }, 5000);
    }
    return () => clearTimeout(timeout);
  }, [error, successMessage]);

  const handleInputChange = (index, value) => {
    if (isLoading) return;
    
    if (value.length <= 1 && /^[0-9]*$/.test(value)) {
      const newCode = [...verificationCode];
      newCode[index] = value;
      setVerificationCode(newCode);
      setError('');
      
      if (value !== '' && index < 5) {
        document.getElementById(`modal-code-${index + 1}`)?.focus();
      } else if (index === 5 && value !== '') {
        handleVerifyCode(newCode);
      }
    }
  };

  const handleKeyDown = (index, event) => {
    if (isLoading) return;
    
    if (event.key === 'Backspace' && verificationCode[index] === '' && index > 0) {
      const newCode = [...verificationCode];
      newCode[index - 1] = '';
      setVerificationCode(newCode);
      document.getElementById(`modal-code-${index - 1}`)?.focus();
    }
  };

  const handlePaste = (event) => {
    if (isLoading) return;
    
    event.preventDefault();
    const pastedData = event.clipboardData.getData('text/plain');
    
    const digits = pastedData.replace(/\D/g, '').slice(0, 6);
    
    if (digits.length > 0) {
      const newCode = [...verificationCode];
      
      for (let i = 0; i < 6; i++) {
        newCode[i] = digits[i] || '';
      }
      
      setVerificationCode(newCode);
      setError('');
      
      const nextEmptyIndex = newCode.findIndex(digit => digit === '');
      if (nextEmptyIndex !== -1) {
        document.getElementById(`modal-code-${nextEmptyIndex}`)?.focus();
      } else {
        document.getElementById('modal-code-5')?.focus();
        if (digits.length === 6) {
          handleVerifyCode(newCode);
        }
      }
    }
  };

  const formatDisplayPhoneNumber = (phone) => {
    if (!phone) return phone;
    const cleaned = phone.replace(/\D/g, '').slice(-10);
    if (cleaned.length < 10) return phone;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  };

  const handleVerifyCode = async (codeArray = verificationCode) => {
    if (isLoading) return;
    
    const code = codeArray.join('');
    
    if (!code || code.length !== 6 || codeArray.some(digit => digit === '')) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Format phone number to ensure it has +1 and only contains digits
      const cleanPhone = phoneNumber.replace(/\D/g, '');
      const formattedPhone = cleanPhone.startsWith('1') ? 
        `+${cleanPhone}` : 
        `+1${cleanPhone.slice(-10)}`;

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/phone/verify`, {
        phoneNumber: formattedPhone,
        verificationCode: code,
        isProfileUpdate: true // Flag to indicate this is for profile update
      });

      if (response.data.success) {
        onConfirm({ success: true, verificationCode: code });
      } else {
        setError(response.data.message || 'Invalid verification code');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setError(error.response?.data?.message || 'An error occurred during verification');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async (channel) => {
    if (!canResend) return;

    setIsLoading(true);
    setError('');
    setSuccessMessage('');
    
    // Clear the verification code inputs
    setVerificationCode(['', '', '', '', '', '']);

    try {
      // Format phone number to ensure it has +1
      const formattedPhone = phoneNumber.startsWith('+1') ? 
        phoneNumber : 
        `+1${phoneNumber.replace(/\D/g, '').slice(-10)}`;

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/phone/send-verification`, {
        phoneNumber: formattedPhone,
        channel,
        isProfileUpdate: true
      });

      if (response.data.success) {
        setSuccessMessage(`Verification code ${channel === 'sms' ? 'sent' : 'requested'} successfully`);
        setTimeLeft(30);
        setCanResend(false);
        
        // Focus the first input box after clearing
        setTimeout(() => {
          document.getElementById('modal-code-0')?.focus();
        }, 100);
      } else {
        setError('Failed to send code. Please try again.');
      }
    } catch (error) {
      console.error('Error sending code:', error);
      if (error.response?.status === 429) {
        setError('Please wait a few minutes before requesting another code');
        setTimeLeft(60);
        setCanResend(false);
      } else {
        setError(error.response?.data?.message || 'An error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getInputBorderColor = () => {
    if (error) return '!border-[#FF3B3B] dark:!border-[#FF3B3B]';
    return '';
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999]">
      <div className="verification-modal bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-[#2a2a2a] p-8 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-black dark:text-white">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex flex-col items-start gap-[25px] w-full">
          <div className="flex flex-col items-start gap-[13px] w-full">
            <p className="text-[16px] md:text-[18px] font-normal text-[#151515] dark:text-white leading-tight">
              Enter the 6-digit code sent to you at
            </p>
            <p className="text-[16px] md:text-[18px] font-normal text-[#151515] dark:text-white leading-tight whitespace-nowrap">
              {formatDisplayPhoneNumber(phoneNumber)}
            </p>
            {error && (
              <div className="flex items-center gap-2 text-[#FF3B3B] dark:text-[#FF3B3B] text-[15px] font-normal">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 5.3335V8.00016M8 10.6668H8.00667M14 8.00016C14 11.3137 11.3137 14.0002 8 14.0002C4.68629 14.0002 2 11.3137 2 8.00016C2 4.68645 4.68629 2.00016 8 2.00016C11.3137 2.00016 14 4.68645 14 8.00016Z" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{error}</span>
              </div>
            )}
            {successMessage && (
              <div className="flex items-center gap-2 text-[#10B981] dark:text-[#10B981] text-[15px] font-normal">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M13.3334 4L6.00008 11.3333L2.66675 8" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{successMessage}</span>
              </div>
            )}
          </div>

          <div className="flex flex-col items-start gap-[25px] w-full">
            <div className="flex gap-[2%] w-full justify-between">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <input
                  key={index}
                  id={`modal-code-${index}`}
                  type="text"
                  maxLength="1"
                  value={verificationCode[index]}
                  onChange={(e) => handleInputChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  style={{ fontFamily: 'var(--chat-font-family, midus-sans, system-ui, sans-serif)' }}
                  className={`verification-code-box w-[15%] aspect-square bg-gray-50/50 dark:bg-[#181818] border border-gray-200 dark:border-[#2a2a2a] ${getInputBorderColor()} text-black dark:text-white text-[24px] font-normal text-center
                    focus:outline-none focus:ring-1 ${error ? 'focus:ring-[#FF3B3B]' : 'focus:ring-[#6b6b6b] dark:focus:ring-[#303030]'}`}
                />
              ))}
            </div>

            <button 
              onClick={() => handleVerifyCode()}
              disabled={isLoading || verificationCode.some(digit => digit === '')}
              className={`verification-btn w-full py-3 text-[16px] font-normal ${
                isLoading || verificationCode.some(digit => digit === '')
                  ? 'bg-[#dfdfdf] dark:bg-[#0d0d0d] text-[#999999] dark:text-[#222222] cursor-not-allowed' 
                  : 'bg-[#151515] dark:bg-white text-white dark:text-black cursor-pointer'
              }`}
            >
              {isLoading ? 'Verifying...' : 'Verify'}
            </button>

            <div className="flex flex-col gap-[8px] w-full">
              <button 
                onClick={() => handleResend('sms')}
                disabled={isLoading || !canResend}
                style={{ fontFamily: 'var(--chat-font-family, midus-sans, system-ui, sans-serif)' }}
                className={`verification-btn h-9 px-4 bg-gray-50/50 dark:bg-[#181818] border border-gray-200 dark:border-[#2a2a2a] text-gray-900 dark:text-white text-[14px] font-normal whitespace-nowrap ${
                  isLoading || !canResend ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-100 dark:hover:bg-[#252525]'
                }`}
              >
                {timeLeft > 0 ? `Resend code via SMS (${timeLeft.toString().padStart(2, '0')})` : 'Resend code via SMS'}
              </button>
              <button 
                onClick={() => handleResend('call')}
                disabled={isLoading || !canResend}
                style={{ fontFamily: 'var(--chat-font-family, midus-sans, system-ui, sans-serif)' }}
                className={`verification-btn h-9 px-4 bg-gray-50/50 dark:bg-[#181818] border border-gray-200 dark:border-[#2a2a2a] text-gray-900 dark:text-white text-[14px] font-normal whitespace-nowrap ${
                  isLoading || !canResend ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-100 dark:hover:bg-[#252525]'
                }`}
              >
                {timeLeft > 0 ? `Call me with code (${timeLeft.toString().padStart(2, '0')})` : 'Call me with code'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default PhoneVerificationModal;
